import type { AgentConfiguratorState } from '../types/agentConfigurator';

export type OptimizationScenario = 'base' | 'optimize_by_token';
export type OptimizationTargetState = 'constant' | 'modifiable';
export type AgentOptimizationMode = 'constant' | 'base' | 'optimize_by_token';

export interface OptimizationPolicyAgent {
  id: string;
  name: string;
  state: OptimizationTargetState;
  scenario?: OptimizationScenario;
}

export interface OptimizationPolicyMetaPromptEntry {
  state: OptimizationTargetState;
  scenario?: OptimizationScenario;
}

export interface OptimizationPolicy {
  scenario_order: OptimizationScenario[];
  agents: OptimizationPolicyAgent[];
  meta_prompts: Record<string, OptimizationPolicyMetaPromptEntry>;
}

export interface OptimizationPolicyUiState {
  agents: Record<string, AgentOptimizationMode>;
  metaPrompts: Record<string, AgentOptimizationMode>;
}

export interface OptimizationAgentTarget {
  key: string;
  id: string;
  name: string;
  role: 'coordinator' | 'worker' | 'async_orchestrator';
}

export interface OptimizationMetaPromptTarget {
  key: string;
  label: string;
}

const META_PROMPT_FRONTEND_TO_POLICY: Record<string, string> = {
  CORE_PROMPT: 'core_prompt',
  MASTER_SUFFIX: 'master_suffix',
  ASYNC_SUFFIX: 'async_suffix',
  SYNC_EXTRA: 'sync_extra',
  FINAL_STEP_PROMPT: 'final_step_prompt',
  RAG_PROMPT: 'rag_tool_description',
  LTM_PROMPT: 'ltm_tool_description',
  DIARY_WRITE_PROMPT: 'diary_write_tool_description',
  DIARY_READ_PROMPT: 'diary_read_tool_description',
  PORTRAIT_WRITE_PROMPT: 'portrait_write_tool_description',
  PORTRAIT_READ_PROMPT: 'portrait_read_tool_description',
};

const META_PROMPT_LABELS: Record<string, string> = {
  core_prompt: 'Core prompt',
  master_suffix: 'Master suffix',
  async_suffix: 'Async suffix',
  sync_extra: 'Sync extra',
  final_step_prompt: 'Final step prompt',
  rag_tool_description: 'RAG tool description',
  ltm_tool_description: 'LTM tool description',
  diary_write_tool_description: 'Diary write tool description',
  diary_read_tool_description: 'Diary read tool description',
  portrait_write_tool_description: 'Portrait write tool description',
  portrait_read_tool_description: 'Portrait read tool description',
  user_message_injection_tool_description: 'User message injection tool description',
};

function coordinatorAgentId(config: AgentConfiguratorState['coordinator']): string {
  const id = typeof config.id === 'string' ? config.id.trim() : '';
  if (id) return id;
  const agentId = typeof config.agent_id === 'string' ? config.agent_id.trim() : '';
  if (agentId) return agentId;
  return 'coordinator';
}

function coordinatorAgentKey(config: AgentConfiguratorState['coordinator']): string {
  return `coordinator:${coordinatorAgentId(config)}`;
}

function workerAgentKey(worker: { id: string }): string {
  return `worker:${worker.id}`;
}

function asyncOrchestratorAgentId(
  asyncOrchestrator: NonNullable<AgentConfiguratorState['async_orchestrator']>
): string {
  const name = typeof asyncOrchestrator.name === 'string' ? asyncOrchestrator.name.trim() : '';
  return name || 'async-orchestrator';
}

function asyncOrchestratorAgentKey(
  asyncOrchestrator: NonNullable<AgentConfiguratorState['async_orchestrator']>
): string {
  return `async:${asyncOrchestratorAgentId(asyncOrchestrator)}`;
}

function isAsyncOrchestratorEnabled(
  asyncOrchestrator: AgentConfiguratorState['async_orchestrator']
): asyncOrchestrator is NonNullable<AgentConfiguratorState['async_orchestrator']> {
  return Boolean(asyncOrchestrator && asyncOrchestrator.enabled !== false);
}

export function listOptimizationAgentTargets(config: AgentConfiguratorState): OptimizationAgentTarget[] {
  const targets: OptimizationAgentTarget[] = [];
  const coordId = coordinatorAgentId(config.coordinator);
  targets.push({
    key: coordinatorAgentKey(config.coordinator),
    id: coordId,
    name: config.coordinator.name?.trim() || 'master',
    role: 'coordinator',
  });

  for (const worker of config.workers) {
    targets.push({
      key: workerAgentKey(worker),
      id: worker.id,
      name: worker.name?.trim() || worker.id,
      role: 'worker',
    });
  }

  if (isAsyncOrchestratorEnabled(config.async_orchestrator)) {
    const asyncId = asyncOrchestratorAgentId(config.async_orchestrator);
    targets.push({
      key: asyncOrchestratorAgentKey(config.async_orchestrator),
      id: asyncId,
      name: config.async_orchestrator.name?.trim() || 'Async orchestrator',
      role: 'async_orchestrator',
    });
  }

  return targets;
}

export function listOptimizationMetaPromptTargets(
  config: AgentConfiguratorState
): OptimizationMetaPromptTarget[] {
  const metaPrompts = config.metaPrompts ?? {};
  const keys = new Set<string>();

  for (const frontendKey of Object.keys(metaPrompts)) {
    const policyKey = META_PROMPT_FRONTEND_TO_POLICY[frontendKey];
    if (policyKey) keys.add(policyKey);
  }

  for (const policyKey of Object.keys(META_PROMPT_LABELS)) {
    const frontendKey = Object.entries(META_PROMPT_FRONTEND_TO_POLICY).find(([, v]) => v === policyKey)?.[0];
    if (frontendKey && metaPrompts[frontendKey] !== undefined) {
      keys.add(policyKey);
    }
  }

  return Array.from(keys)
    .sort()
    .map((key) => ({
      key,
      label: META_PROMPT_LABELS[key] ?? key,
    }));
}

export function createDefaultOptimizationPolicyUi(
  config: AgentConfiguratorState,
  defaultModifiableScenario: OptimizationScenario = 'base'
): OptimizationPolicyUiState {
  const agents: Record<string, AgentOptimizationMode> = {};
  for (const target of listOptimizationAgentTargets(config)) {
    agents[target.key] = defaultModifiableScenario;
  }

  const metaPrompts: Record<string, AgentOptimizationMode> = {};
  for (const target of listOptimizationMetaPromptTargets(config)) {
    metaPrompts[target.key] = defaultModifiableScenario;
  }

  return { agents, metaPrompts };
}

export function syncOptimizationPolicyUi(
  config: AgentConfiguratorState,
  prev: OptimizationPolicyUiState,
  defaultModifiableScenario: OptimizationScenario = 'base'
): OptimizationPolicyUiState {
  const agents = { ...prev.agents };
  for (const target of listOptimizationAgentTargets(config)) {
    if (!(target.key in agents)) {
      agents[target.key] = defaultModifiableScenario;
    }
  }

  for (const key of Object.keys(agents)) {
    if (!listOptimizationAgentTargets(config).some((target) => target.key === key)) {
      delete agents[key];
    }
  }

  const metaPrompts = { ...prev.metaPrompts };
  for (const target of listOptimizationMetaPromptTargets(config)) {
    if (!(target.key in metaPrompts)) {
      metaPrompts[target.key] = defaultModifiableScenario;
    }
  }

  for (const key of Object.keys(metaPrompts)) {
    if (!listOptimizationMetaPromptTargets(config).some((target) => target.key === key)) {
      delete metaPrompts[key];
    }
  }

  return { agents, metaPrompts };
}

function modeToPolicyEntry(mode: AgentOptimizationMode): OptimizationPolicyMetaPromptEntry {
  if (mode === 'constant') return { state: 'constant' };
  return { state: 'modifiable', scenario: mode };
}

function modeToAgentEntry(
  id: string,
  name: string,
  mode: AgentOptimizationMode
): OptimizationPolicyAgent {
  if (mode === 'constant') {
    return { id, name, state: 'constant' };
  }
  return { id, name, state: 'modifiable', scenario: mode };
}

function deriveScenarioOrder(modes: AgentOptimizationMode[]): OptimizationScenario[] {
  const hasToken = modes.some((mode) => mode === 'optimize_by_token');
  const hasBase = modes.some((mode) => mode === 'base');
  const order: OptimizationScenario[] = [];
  if (hasToken) order.push('optimize_by_token');
  if (hasBase) order.push('base');
  return order.length ? order : ['base'];
}

export function buildOptimizationPolicy(
  config: AgentConfiguratorState,
  ui: OptimizationPolicyUiState
): OptimizationPolicy {
  const agents = listOptimizationAgentTargets(config).map((target) =>
    modeToAgentEntry(target.id, target.name, ui.agents[target.key] ?? 'base')
  );

  const meta_prompts: Record<string, OptimizationPolicyMetaPromptEntry> = {};
  for (const target of listOptimizationMetaPromptTargets(config)) {
    meta_prompts[target.key] = modeToPolicyEntry(ui.metaPrompts[target.key] ?? 'base');
  }

  const modifiableModes = [
    ...Object.values(ui.agents),
    ...Object.values(ui.metaPrompts),
  ].filter((mode) => mode !== 'constant');

  return {
    scenario_order: deriveScenarioOrder(modifiableModes),
    agents,
    meta_prompts,
  };
}

export function deriveOptimizeByTokenFromPolicy(policy: OptimizationPolicy): boolean {
  return policy.scenario_order[0] === 'optimize_by_token';
}

export function hasModifiableOptimizationTargets(policy: OptimizationPolicy): boolean {
  return (
    policy.agents.some((agent) => agent.state === 'modifiable') ||
    Object.values(policy.meta_prompts).some((entry) => entry.state === 'modifiable')
  );
}

export function applyBulkModifiableScenario(
  ui: OptimizationPolicyUiState,
  scenario: OptimizationScenario,
  config?: AgentConfiguratorState
): OptimizationPolicyUiState {
  const agents = { ...ui.agents };
  if (config) {
    for (const target of listOptimizationAgentTargets(config)) {
      agents[target.key] = scenario;
    }
  } else {
    for (const key of Object.keys(agents)) {
      agents[key] = scenario;
    }
  }

  const metaPrompts = { ...ui.metaPrompts };
  if (config) {
    for (const target of listOptimizationMetaPromptTargets(config)) {
      metaPrompts[target.key] = scenario;
    }
  } else {
    for (const key of Object.keys(metaPrompts)) {
      metaPrompts[key] = scenario;
    }
  }

  return { agents, metaPrompts };
}

export function applyBulkFrozen(
  ui: OptimizationPolicyUiState,
  config?: AgentConfiguratorState
): OptimizationPolicyUiState {
  const agents = { ...ui.agents };
  if (config) {
    for (const target of listOptimizationAgentTargets(config)) {
      agents[target.key] = 'constant';
    }
  } else {
    for (const key of Object.keys(agents)) {
      agents[key] = 'constant';
    }
  }

  const metaPrompts = { ...ui.metaPrompts };
  if (config) {
    for (const target of listOptimizationMetaPromptTargets(config)) {
      metaPrompts[target.key] = 'constant';
    }
  } else {
    for (const key of Object.keys(metaPrompts)) {
      metaPrompts[key] = 'constant';
    }
  }

  return { agents, metaPrompts };
}
